/**
 * Support Onboarding Demo - internal mock result workspace.
 * Shows the future support workspace state with fake data only.
 * No backend, no storage, no real invoicing, no banking details saved.
 */

interface DemoField {
  label: string;
  value: string;
}

interface DemoPanel {
  title: string;
  eyebrow: string;
  status: string;
  statusTone: 'success' | 'warning' | 'danger' | 'muted';
  fields: DemoField[];
}

function injectStyles(): void {
  if (document.getElementById('sp-demo-styles')) return;
  const style = document.createElement('style');
  style.id = 'sp-demo-styles';
  style.textContent = `
body.sp-public-body {
  display: block !important;
  min-height: 100dvh;
  height: auto !important;
  overflow-y: auto !important;
  background: var(--bg) !important;
}
.sp-public-root {
  display: block !important;
  width: 100% !important;
  min-height: 100dvh !important;
  height: auto !important;
  overflow: visible !important;
  flex: none !important;
  background: var(--bg);
}
.sp-demo-page {
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: var(--bg);
  color: var(--fg);
  font-family: var(--font-sans);
  padding: 24px;
}
.sp-demo-shell {
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
}
.sp-demo-topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
}
.sp-demo-brand {
  color: var(--fg-muted);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
.sp-demo-header h1 {
  margin: 6px 0 6px;
  color: var(--fg);
  font-size: 24px;
  line-height: 1.2;
  font-weight: 800;
}
.sp-demo-header p {
  margin: 0;
  color: var(--fg-muted);
  font-size: 14px;
  line-height: 1.45;
}
.sp-demo-badges {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}
.sp-demo-badge {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 3px 9px;
  border-radius: 4px;
  border: 1px solid var(--border);
  background: var(--bg-secondary);
  color: var(--fg-muted);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
}
.sp-demo-badge-warning {
  border-color: var(--warning);
  color: var(--warning);
}
.sp-demo-warning {
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid var(--warning);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--fg);
  font-size: 13px;
  line-height: 1.45;
}
.sp-demo-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.sp-demo-card {
  min-width: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-secondary);
  padding: 14px;
}
.sp-demo-card-wide {
  grid-column: 1 / -1;
}
.sp-demo-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.sp-demo-eyebrow {
  color: var(--fg-muted);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.sp-demo-card-title {
  margin-top: 3px;
  color: var(--fg);
  font-size: 15px;
  font-weight: 800;
}
.sp-demo-status {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  min-height: 22px;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid var(--border);
  background: var(--bg-tertiary);
  color: var(--fg-muted);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.sp-demo-status-success {
  border-color: var(--success);
  color: var(--success);
}
.sp-demo-status-warning {
  border-color: var(--warning);
  color: var(--warning);
}
.sp-demo-status-danger {
  border-color: var(--danger);
  color: var(--danger);
}
.sp-demo-fields {
  display: grid;
  gap: 8px;
}
.sp-demo-field {
  display: grid;
  grid-template-columns: minmax(112px, 34%) minmax(0, 1fr);
  gap: 10px;
  align-items: baseline;
  min-width: 0;
}
.sp-demo-label {
  color: var(--fg-muted);
  font-size: 12px;
  font-weight: 700;
}
.sp-demo-value {
  min-width: 0;
  color: var(--fg);
  font-size: 13px;
  line-height: 1.35;
  word-break: break-word;
}
.sp-demo-invoice {
  border-style: dashed;
}
.sp-demo-invoice-banner {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid var(--danger);
  background: var(--bg-tertiary);
}
.sp-demo-invoice-title {
  color: var(--fg);
  font-size: 14px;
  font-weight: 800;
}
.sp-demo-invoice-warning {
  color: var(--danger);
  font-size: 11px;
  font-weight: 800;
  text-align: right;
}
.sp-demo-invoice-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.sp-demo-invoice-table th {
  padding: 8px;
  border-bottom: 1px solid var(--border);
  color: var(--fg-muted);
  font-size: 11px;
  text-align: left;
  text-transform: uppercase;
}
.sp-demo-invoice-table td {
  padding: 8px;
  border-bottom: 1px solid var(--border);
  color: var(--fg);
}
.sp-demo-invoice-table th:last-child,
.sp-demo-invoice-table td:last-child {
  text-align: right;
}
.sp-demo-invoice-total {
  display: flex;
  justify-content: flex-end;
  gap: 18px;
  margin-top: 12px;
  color: var(--fg);
  font-size: 15px;
  font-weight: 800;
}
.sp-demo-invoice-footer {
  margin-top: 10px;
  color: var(--fg-muted);
  font-size: 12px;
}
.sp-demo-review-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.sp-demo-review-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
}
.sp-demo-review-name {
  color: var(--fg-muted);
  font-size: 12px;
  font-weight: 700;
}
.sp-demo-review-value {
  color: var(--fg);
  font-size: 12px;
  font-weight: 700;
  text-align: right;
}
.sp-demo-footer {
  margin-top: 16px;
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--fg-muted);
  font-size: 12px;
  line-height: 1.6;
}
.sp-demo-footer strong {
  color: var(--fg);
}
@media (max-width: 820px) {
  .sp-demo-page { padding: 16px; }
  .sp-demo-topbar { flex-direction: column; }
  .sp-demo-badges { justify-content: flex-start; }
  .sp-demo-grid { grid-template-columns: 1fr; }
  .sp-demo-field { grid-template-columns: 1fr; gap: 2px; }
  .sp-demo-invoice-banner { flex-direction: column; align-items: flex-start; }
  .sp-demo-invoice-warning { text-align: left; }
  .sp-demo-review-list { grid-template-columns: 1fr; }
}
`;
  document.head.appendChild(style);
}

function esc(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

function shell(): HTMLElement {
  document.title = 'Support Onboarding Demo - Hub People';
  injectStyles();

  document.body.classList.add('sp-public-body');
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.classList.add('sp-public-root');
  root.innerHTML = '';

  const hide = (id: string): void => {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  };
  hide('sidebar-scrim');
  hide('modal-overlay');
  hide('toast');

  const page = document.createElement('div');
  page.className = 'sp-demo-page';
  root.appendChild(page);
  return page;
}

function renderFields(fields: DemoField[]): string {
  return fields.map(field => `
    <div class="sp-demo-field">
      <div class="sp-demo-label">${esc(field.label)}</div>
      <div class="sp-demo-value">${esc(field.value)}</div>
    </div>
  `).join('');
}

function renderPanel(panel: DemoPanel): string {
  return `
    <section class="sp-demo-card" aria-label="${esc(panel.title)}">
      <div class="sp-demo-card-head">
        <div>
          <div class="sp-demo-eyebrow">${esc(panel.eyebrow)}</div>
          <div class="sp-demo-card-title">${esc(panel.title)}</div>
        </div>
        <div class="sp-demo-status sp-demo-status-${esc(panel.statusTone)}">${esc(panel.status)}</div>
      </div>
      <div class="sp-demo-fields">${renderFields(panel.fields)}</div>
    </section>
  `;
}

export function isSupportOnboardingDemoPath(): boolean {
  return /\/support-onboarding-demo\/?$/.test(window.location.pathname);
}

export function renderSupportOnboardingDemo(): void {
  const panels: DemoPanel[] = [
    {
      title: 'Support Person Profile',
      eyebrow: 'Panel 1',
      status: 'Profile registered (demo)',
      statusTone: 'success',
      fields: [
        { label: 'Name', value: 'Jane Smith (fake)' },
        { label: 'Role', value: 'Peer support worker' },
        { label: 'Email', value: 'jane.smith@example.com (fake)' },
        { label: 'Phone', value: '0400 000 000 (fake)' },
        { label: 'ABN', value: '00 000 000 000 (fake)' },
        { label: 'Business', value: 'Demo Support Services (fake)' },
        { label: 'Status', value: 'Profile registered (demo)' },
        { label: 'Qualifications', value: 'Certificate IV Community Services (fake)' },
      ],
    },
    {
      title: 'Payment Details',
      eyebrow: 'Panel 2',
      status: 'Demo placeholder',
      statusTone: 'warning',
      fields: [
        { label: 'BSB', value: 'XXX-XXX (not saved)' },
        { label: 'Account', value: 'XXXX-XXXX (not saved)' },
        { label: 'Name', value: 'J Smith (fake)' },
        { label: 'Status', value: 'Not verified - demo placeholder only' },
      ],
    },
    {
      title: 'Support Agreement',
      eyebrow: 'Panel 3',
      status: 'Agreement active (demo)',
      statusTone: 'success',
      fields: [
        { label: 'Participant', value: 'Demo Participant' },
        { label: 'Provider', value: 'Jane Smith (Demo Support Services)' },
        { label: 'Type', value: 'One-time support booking' },
        { label: 'Start', value: '2026-06-01 (fake)' },
        { label: 'Cancellation', value: '24 hours notice' },
        { label: 'Status', value: 'Agreement active (demo)' },
      ],
    },
    {
      title: 'Job / Session',
      eyebrow: 'Panel 4',
      status: 'Scheduled (demo)',
      statusTone: 'success',
      fields: [
        { label: 'Date', value: '2026-06-05 (fake)' },
        { label: 'Time', value: '10:00 AM - 2:00 PM (4 hours)' },
        { label: 'Type', value: 'Community participation' },
        { label: 'Location', value: 'Participant area (fake)' },
        { label: 'Travel', value: '25 km round trip' },
        { label: 'Status', value: 'Scheduled (demo)' },
      ],
    },
    {
      title: 'NDIS / Support Item',
      eyebrow: 'Panel 5',
      status: 'Requires review',
      statusTone: 'warning',
      fields: [
        { label: 'Category', value: 'Core Supports - Community Participation' },
        { label: 'Item', value: '04_104_0125_6_1 (example code only)' },
        { label: 'Rate', value: 'Placeholder - verify against NDIS Support Catalogue' },
        { label: 'Status', value: 'Not confirmed - requires review' },
      ],
    },
  ];

  const reviewRows: DemoField[] = [
    { label: 'Profile', value: 'Registered (demo)' },
    { label: 'Agreement', value: 'Active (demo)' },
    { label: 'Job', value: 'Scheduled (demo)' },
    { label: 'Invoice', value: 'Not generated' },
    { label: 'NDIS claim', value: 'Not submitted' },
    { label: 'Payment', value: 'Not processed' },
    { label: 'Review', value: 'Awaiting authorised review' },
  ];

  const page = shell();
  page.innerHTML = `
    <main class="sp-demo-shell">
      <div class="sp-demo-topbar">
        <header class="sp-demo-header">
          <div class="sp-demo-brand">the hub ecosystems</div>
          <h1>Support Onboarding Demo</h1>
          <p>Internal test workspace - fake data only</p>
        </header>
        <div class="sp-demo-badges" aria-label="Demo warnings">
          <span class="sp-demo-badge sp-demo-badge-warning">Auth-gated</span>
          <span class="sp-demo-badge sp-demo-badge-warning">No backend storage</span>
          <span class="sp-demo-badge sp-demo-badge-warning">No invoice generated</span>
        </div>
      </div>

      <div class="sp-demo-warning">
        Demo only. Fake data only. Auth-gated. No backend storage. No banking details saved.
        No invoice generated. Not payable. No NDIS claim submitted. Internal review only.
      </div>

      <div class="sp-demo-grid">
        ${panels.map(renderPanel).join('')}

        <section class="sp-demo-card sp-demo-card-wide sp-demo-invoice" aria-label="Invoice Preview">
          <div class="sp-demo-card-head">
            <div>
              <div class="sp-demo-eyebrow">Panel 6</div>
              <div class="sp-demo-card-title">Invoice Preview</div>
            </div>
            <div class="sp-demo-status sp-demo-status-danger">Not payable</div>
          </div>
          <div class="sp-demo-invoice-banner">
            <div class="sp-demo-invoice-title">DEMO INVOICE PREVIEW</div>
            <div class="sp-demo-invoice-warning">NOT PAYABLE - NOT GENERATED - NO CLAIM SUBMITTED</div>
          </div>
          <table class="sp-demo-invoice-table">
            <thead>
              <tr>
                <th>Line item</th>
                <th>Quantity</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Community Participation</td>
                <td>4h</td>
                <td>$67.56</td>
                <td>$270.24</td>
              </tr>
              <tr>
                <td>Provider travel</td>
                <td>25km</td>
                <td>$0.97</td>
                <td>$24.25</td>
              </tr>
            </tbody>
          </table>
          <div class="sp-demo-invoice-total">
            <span>Total</span>
            <span>$294.49</span>
          </div>
          <div class="sp-demo-invoice-footer">Demo only. Not a real invoice. Placeholder calculation.</div>
        </section>

        <section class="sp-demo-card sp-demo-card-wide" aria-label="Review and Status">
          <div class="sp-demo-card-head">
            <div>
              <div class="sp-demo-eyebrow">Panel 7</div>
              <div class="sp-demo-card-title">Review / Status</div>
            </div>
            <div class="sp-demo-status sp-demo-status-warning">Awaiting review</div>
          </div>
          <div class="sp-demo-review-list">
            ${reviewRows.map(row => `
              <div class="sp-demo-review-item">
                <span class="sp-demo-review-name">${esc(row.label)}</span>
                <span class="sp-demo-review-value">${esc(row.value)}</span>
              </div>
            `).join('')}
          </div>
        </section>
      </div>

      <footer class="sp-demo-footer">
        <strong>This is a demo workspace showing mock data.</strong><br>
        No real data exists. No backend storage. No invoice generated.<br>
        No banking details saved. No NDIS claim submitted.<br>
        Internal review only - auth-gated.
      </footer>
    </main>
  `;
}
